/**
 * @file UserCaching
 * @description
 *   Caches `CpmCUserApiModel` objects by UUID so that repeated look‑ups
 *   avoid round‑trips to the server.
 *
 *   The cache is simple – a plain object whose keys are the user UUIDs.
 *   All cache operations are performed via static members, which means
 *   the cache is shared across the entire application without the need
 *   for an Angular service instance.
 *
 *   The main entry point is {@link UserCaching.getUsers}.  It returns an
 *   `Observable<CpmCUserApiModel[]>` that always emits **exactly** the
 *   users requested (in the same order as the `userUUIDs` array).  If a
 *   user is already in the cache, it is served immediately; otherwise
 *   the API is called for the missing UUIDs, the results are cached, and
 *   the combined list is returned.
 *
 *   The implementation intentionally uses RxJS operators only where
 *   necessary – the observable chain is short and side‑effect free.
 */

import {lastValueFrom, Observable, of, Subject} from 'rxjs';
import {map, tap} from 'rxjs/operators';

import {CpmCUserApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-user.api-model';
import {CpmCUserApiService} from '../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-user.api-service';
import {CpmCPaginatedResultAPISchema} from '../../../gen/cpm-c-api-ts/src/schemas/cpmc-paginated-result.api-schemas';

/**
 * Simple in‑memory cache for `CpmCUserApiModel` objects keyed by UUID.
 */
export abstract class UserCaching {
    private static readonly _batchQueue: {
        id: string;
        resultSubject: Subject<CpmCUserApiModel | undefined>;
    }[] = [];

    private static _users: Record<string, CpmCUserApiModel> = {};
    private static _flushTimer: ReturnType<typeof setTimeout> | null = null;

    // ===============================================================
    // ===============================================================

    /**
     * Return a list of users by UUID.
     *
     * The method works in two phases:
     *
     * 1. **Cache lookup** – All requested UUIDs are checked against the
     *    static `users` cache.  If every UUID is present, the method
     *    immediately returns an `Observable` that emits the cached
     *    instances (`of(cachedResult)`).
     *
     * 2. **Server fetch** – For UUIDs missing from the cache the API is
     *    called via `CpmCUserApiService.getUserList`.  Once the
     *    observable resolves:
     *    - Each received user is stored in the static cache.
     *    - A final array that contains **all** requested users
     *      (cached + newly fetched) is emitted.
     *
     * @param userUUIDs Array of UUIDs that the caller needs.
     * @returns `Observable` that emits an array of `CpmCUserApiModel`
     *          instances, ordered exactly as `userUUIDs` was supplied.
     *
     * @example
     * ```ts
     * UserCaching.getUsers(['uuid1', 'uuid2']).subscribe(users => {
     *   // users[0] === CpmCUserApiModel for 'uuid1'
     *   // users[1] === CpmCUserApiModel for 'uuid2'
     * });
     * ```
     */
    public static getUsers(userUUIDs: string[]): Observable<CpmCUserApiModel[]> {
        // Find the UUIDs that are not yet cached.
        const missingUsers = userUUIDs.filter((uuid) => !(uuid in this._users));

        // If nothing is missing, serve from cache immediately.
        if (missingUsers.length === 0) {
            const cachedResult = userUUIDs.map((uuid) => this._users[uuid]);
            return of(cachedResult);
        }

        if (missingUsers.find((x) => x === undefined || x === null))
            throw new Error('UUIDs can not be null or undefined');

        // Build the API request for the missing UUIDs.
        const observable = CpmCUserApiService.getUserListByUUID(missingUsers, {limit: -1});

        // Cache the response and return a merged result.
        return observable.pipe(
            // Side‑effect: store every fetched user in the static cache.
            tap((fetchedUsers: CpmCPaginatedResultAPISchema<CpmCUserApiModel>) => {
                fetchedUsers.data.forEach((user) => {
                    this._users[user.uuid] = user;
                });
            }),
            // Final emission: merge cached + fetched users, preserving order.
            map(() => userUUIDs.map((uuid) => UserCaching._users[uuid]))
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * Return a list of users by UUID.
     *
     * If the requested users are already in the static cache, the
     * method returns an `Observable` that emits the cached instances
     * immediately (`of(cachedResult)`).  Otherwise the request is
     * queued and will be merged with any other pending requests that
     * arrive within 50 ms.
     *
     * @param userUUID UUID that the caller needs.
     * @returns `Observable` that emits an array of `CpmCUserApiModel`
     *          instances, ordered exactly as `userUUIDs` was supplied.
     */
    public static getUserBatched(userUUID: string): Observable<CpmCUserApiModel | undefined> {
        if (userUUID === undefined || userUUID === null) {
            throw new Error('UUIDs cannot be null or undefined');
        }

        if (userUUID in UserCaching._users) return of(UserCaching._users[userUUID]);

        const result$ = new Subject<CpmCUserApiModel | undefined>();
        this._batchQueue.push({
            id: userUUID,
            resultSubject: result$
        });

        if (!UserCaching._flushTimer) {
            UserCaching._flushTimer = setTimeout(() => UserCaching._flushBatch(), 50);
        }

        return result$.asObservable();
    }
    /**
     * Flush all queued requests: group all missing UUIDs, call the API
     * once, then resolve every queued `Subject` with the merged result.
     */
    private static _flushBatch(): void {
        const currentBatch = UserCaching._batchQueue.splice(0, UserCaching._batchQueue.length);
        UserCaching._flushTimer = null;

        if (!currentBatch.length) return; // nothing to do

        const uuidsToRequest = Array.from(new Set(currentBatch.map((req) => req.id)));
        lastValueFrom(UserCaching.getUsers(uuidsToRequest))
            .then(() => {
                currentBatch.forEach((req) => {
                    req.resultSubject.next(req.id in UserCaching._users ? UserCaching._users[req.id] : undefined);
                    req.resultSubject.complete();
                });
            })
            .catch(() => {
                currentBatch.forEach((req) => {
                    req.resultSubject.next(undefined);
                    req.resultSubject.complete();
                });
            });
    }
}
